import { Storybook } from "@tento-ui/ui/storybook";
import { Toast } from "@tento-ui/components/toast/toast";
import { ProposalDetail } from "./proposal-detail";
import { ProposalList } from "./proposal-list";

export class ProposalListStoryBook extends Storybook.Default {
  title(): string {
    return "Proposal List";
  }

  description(): Option<string> {
    return `Segmented pill bar for agent-generated scheduling proposals.
Clicking a pill swaps the detail card below. The card shows rationale,
assignment table, and a Schedule CTA.`;
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "Three proposals",
        render: () => {
          return ProposalList.makeWith({
            proposals: sampleProposals(),
            delegate: {
              onProposalFocus: (i) => console.log("focus", i),
              onSelect: (i) =>
                Toast.info(`Selected: ${sampleProposals()[i].label}`),
            },
          });
        },
      },
      {
        title: "Single proposal",
        render: () => {
          return ProposalList.makeWith({
            proposals: [
              {
                id: "A",
                label: "Mon 9am",
                rationale:
                  "First open 60-minute block in your week. No meetings on your calendar.",
                assignments: [
                  {
                    taskId: "aaaa",
                    displayId: "T-12",
                    title: "Write quarterly report",
                    timeLabel: "Mon 9:00–10:00",
                  },
                ],
              },
            ],
            delegate: {
              onSelect: () => Toast.info("Scheduled"),
            },
          });
        },
      },
      {
        title: "Detail card standalone",
        render: () => {
          return ProposalDetail.makeWith({
            rationale:
              "Frontloads the hardest work into your best focus window. Afternoon is completely free.",
            assignments: [
              { displayId: "T-12", title: "Write quarterly report", timeLabel: "Mon 9:00–10:00" },
              { displayId: "T-14", title: "Review PR #42", timeLabel: "Mon 10:00–10:30" },
              { displayId: "T-15", title: "Plan onboarding deck", timeLabel: "Mon 11:00–12:00" },
            ],
            actionLabel: "Select",
            onSelect: () => Toast.info("Scheduled"),
          });
        },
      },
      {
        title: "Without delegate (inert preview)",
        render: () =>
          ProposalList.makeWith({
            proposals: sampleProposals().slice(0, 2),
          }),
      },
    ];
  }
}

function sampleProposals(): ProposalList.Proposal[] {
  return [
    {
      id: "A",
      label: "A · Morning",
      rationale:
        "Frontloads the hardest work into your best focus window. Afternoon is completely free for recovery and ad-hoc meetings.",
      assignments: [
        {
          taskId: "aaaa-1",
          displayId: "T-12",
          title: "Write quarterly report",
          timeLabel: "Mon 9:00–10:00",
        },
        {
          taskId: "aaaa-2",
          displayId: "T-14",
          title: "Review PR #42",
          timeLabel: "Mon 10:00–10:30",
        },
        {
          taskId: "aaaa-3",
          displayId: "T-15",
          title: "Plan onboarding deck",
          timeLabel: "Mon 11:00–12:00",
        },
      ],
    },
    {
      id: "B",
      label: "B · Spread",
      rationale:
        "One task per morning across the week. Gives you recovery time between deep-work sessions and keeps afternoons open.",
      assignments: [
        {
          taskId: "bbbb-1",
          displayId: "T-12",
          title: "Write quarterly report",
          timeLabel: "Mon 9:00–10:00",
        },
        {
          taskId: "bbbb-2",
          displayId: "T-14",
          title: "Review PR #42",
          timeLabel: "Tue 9:00–9:30",
        },
        {
          taskId: "bbbb-3",
          displayId: "T-15",
          title: "Plan onboarding deck",
          timeLabel: "Wed 9:00–10:00",
        },
      ],
    },
    {
      id: "C",
      label: "C · Afternoon",
      rationale:
        "Batches all focus work into a single contiguous afternoon block. Mornings stay free for standup and email triage.",
      assignments: [
        {
          taskId: "cccc-1",
          displayId: "T-12",
          title: "Write quarterly report",
          timeLabel: "Mon 1:00–2:00p",
        },
        {
          taskId: "cccc-2",
          displayId: "T-14",
          title: "Review PR #42",
          timeLabel: "Mon 2:00–2:30p",
        },
        {
          taskId: "cccc-3",
          displayId: "T-15",
          title: "Plan onboarding deck",
          timeLabel: "Mon 3:00–4:00p",
        },
      ],
    },
  ];
}
